/**
 * Signup form. Per FR-002, FR-003, FR-007, FR-010.
 * Email + password + name; submits to Cognito via Amplify.
 */

import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { z } from "zod";

import { FormError } from "@/components/FormError";
import { doSignUp } from "@/lib/auth/useAuth";

const schema = z.object({
  first_name: z.string().min(1, "First name is required"),
  last_name: z.string().min(1, "Last name is required"),
  email: z.string().email("Enter a valid email address"),
  password: z
    .string()
    .min(8, "At least 8 characters")
    .regex(/[a-z]/, "Include at least one lowercase letter")
    .regex(/[A-Z]/, "Include at least one uppercase letter")
    .regex(/[0-9]/, "Include at least one number")
    .regex(/[^a-zA-Z0-9]/, "Include at least one symbol"),
});

type FormValues = z.infer<typeof schema>;

export function SignUp() {
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const onSubmit = async (values: FormValues) => {
    setSubmitting(true);
    setServerError(null);
    const result = await doSignUp(values);
    setSubmitting(false);
    if (!result.ok) {
      setServerError(result.error ?? "Signup failed");
      return;
    }
    if (result.needsConfirm) {
      navigate(`/confirm?email=${encodeURIComponent(values.email)}`);
    } else {
      navigate("/signin");
    }
  };

  return (
    <div className="mx-auto max-w-md px-5 py-10 md:py-16">
      <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-starz-gold">
        Create account
      </p>
      <h1 className="mb-3 font-serif text-3xl text-starz-navy md:text-4xl">
        Start your <em className="text-starz-gold">remodel</em>.
      </h1>
      <div className="mb-7 h-0.5 w-14 bg-starz-gold" aria-hidden="true" />

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label="First name" error={errors.first_name?.message}>
            <input
              type="text"
              autoComplete="given-name"
              {...register("first_name")}
              className={inputClass(!!errors.first_name)}
            />
          </Field>
          <Field label="Last name" error={errors.last_name?.message}>
            <input
              type="text"
              autoComplete="family-name"
              {...register("last_name")}
              className={inputClass(!!errors.last_name)}
            />
          </Field>
        </div>
        <Field label="Email" error={errors.email?.message}>
          <input
            type="email"
            autoComplete="email"
            {...register("email")}
            className={inputClass(!!errors.email)}
          />
        </Field>
        <Field label="Password" error={errors.password?.message}>
          <input
            type="password"
            autoComplete="new-password"
            {...register("password")}
            className={inputClass(!!errors.password)}
          />
        </Field>

        <FormError message={serverError} />

        <button
          type="submit"
          disabled={submitting}
          className="w-full rounded-lg bg-starz-navy px-5 py-3 font-medium text-starz-off-white hover:opacity-90 disabled:opacity-50"
        >
          {submitting ? "Creating account…" : "Create account"}
        </button>
      </form>

      <p className="mt-6 text-center text-sm text-warm-gray">
        Already have an account?{" "}
        <Link to="/signin" className="font-medium text-starz-navy hover:text-starz-gold">
          Sign in
        </Link>
      </p>
    </div>
  );
}

function inputClass(hasError: boolean) {
  return [
    "w-full rounded-lg border bg-white px-4 py-2.5 text-starz-charcoal",
    "focus:outline-none focus:ring-2 focus:ring-starz-gold focus:ring-offset-2",
    hasError ? "border-starz-brick" : "border-starz-gold/25",
  ].join(" ");
}

interface FieldProps {
  label: string;
  error?: string;
  children: React.ReactNode;
}

function Field({ label, error, children }: FieldProps) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold uppercase tracking-widest text-warm-gray">
        {label} {error && <span className="text-starz-gold">*</span>}
      </span>
      {children}
      {error && <span className="mt-1 block text-xs text-starz-brick">{error}</span>}
    </label>
  );
}
